/**
 * BED LINK DIALOG
 * Dialog modal d'émission à froid d'un lien de paiement BED (TICKET-BED-ENTRY).
 *
 * Parcours :
 *   1. L'admin saisit le montant du solde (EUR, auto-calcul différé BED-10),
 *      choisit un payeur optionnel et la langue de l'email (fr/en/es).
 *   2. Submit -> useBedActions().generateBedLink -> RPC create_bed_payment_case
 *      (00130) : crée le payment_case BED + bed_token + timeline.
 *   3. Après succès : affichage du lien + « Copier le lien » + « Envoyer le lien
 *      BED » (Edge Function send-bed-link -> 4LT -> Resend i18n). L'envoi ne
 *      transporte QUE { payment_case_id, locale } : destinataire et token sont
 *      résolus côté serveur (TICKET-BED-SEC-01 finding #1 — plus de relais
 *      email arbitraire possible depuis le navigateur).
 *
 * Le lien /paiement/bed?token= n'est JAMAIS traduit (slug FR universel) ; seule
 * la langue de l'EMAIL change (sélecteur ci-dessous, aucune langue n'étant
 * stockée sur le contact/crew).
 *
 * Modèle : ConfirmPaymentDialog (RHF + Zod, Select payeur, toast Sonner).
 */

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { Copy, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Form, FormSelect, toast } from '../ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useBedActions } from '../../hooks/useBedActions';
import type { BedSoldeBreakdown } from '../../hooks/useBedActions';
import { useSupabase } from '../../hooks/useSupabase';
import { usePaymentCaseContacts } from '../../hooks/usePaymentCaseContacts';
import type { PayerCandidate } from '../../hooks/usePaymentCaseContacts';

// Langues supportées par les templates email BED côté 4LT (Resend + next-intl).
const EMAIL_LOCALES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
] as const;

// Le montant du solde N'EST PLUS saisi (GAP-1 / CDC §A) : il est calculé
// serveur-side (get_bed_solde) et affiché en lecture seule. Le formulaire ne
// porte donc que le payeur (optionnel) et la langue de l'email.
const bedLinkSchema = z.object({
  payerContactId: z.string(),
  locale: z.enum(['fr', 'en', 'es']),
});

// Formatage EUR d'un montant en centimes.
function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  });
}

type BedLinkFormValues = z.infer<typeof bedLinkSchema>;

interface BedLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Porteur du dossier BED (un seul des deux est défini, aligné PaymentSection).
  crewId?: string;
  contactId?: string;
  // Dossier BPI payé d'où l'on source les contacts candidats (payeur) et le
  // destinataire de l'email. Le dossier BED n'existe pas encore à l'ouverture.
  paidBpiCaseId: string;
  prospectName: string;
  productName: string;
  // Rafraîchit la liste des payment_cases. Appelé à la FERMETURE du dialog
  // (pas à la génération) : un refresh immédiat ferait apparaître le BED
  // draft, démonterait le dialog et perdrait le lien avant copie/envoi.
  onGenerated: () => void;
}

function formatCandidateLabel(candidate: PayerCandidate): string {
  const fullName = `${candidate.first_name ?? ''} ${candidate.last_name ?? ''}`.trim() || 'Contact';
  if (candidate.role === 'pilot') return `${fullName} (pilote)`;
  if (candidate.role === 'copilot') return `${fullName} (copilote)`;
  return fullName;
}

// Mapping des erreurs de l'Edge send-bed-link -> message FR. Le destinataire
// et le token sont résolus côté serveur : on traduit ici les refus métier
// (410 = aucun lien actif, 422 = statut/destinataire) en messages actionnables.
async function mapSendBedLinkError(err: unknown): Promise<string> {
  if (err instanceof FunctionsHttpError) {
    const context = err.context as Response;
    let code: string | undefined;
    try {
      code = ((await context.json()) as { error?: string }).error;
    } catch {
      // Body non-JSON : on retombe sur le mapping par statut HTTP seul.
    }
    if (context.status === 410) {
      return "Aucun lien BED actif pour ce dossier — régénérez le lien avant l'envoi.";
    }
    if (context.status === 422) {
      if (code === 'recipient_email_missing' || code === 'no_recipient') {
        return 'Aucune adresse email enregistrée pour le destinataire.';
      }
      return "Le statut du dossier ne permet plus l'envoi du lien BED.";
    }
  }
  return "Erreur lors de l'envoi du lien BED.";
}

export function BedLinkDialog({
  open,
  onOpenChange,
  crewId,
  contactId,
  paidBpiCaseId,
  prospectName,
  productName,
  onGenerated,
}: BedLinkDialogProps) {
  const supabase = useSupabase();
  const { generateBedLink, fetchBedSolde } = useBedActions();
  const [submitting, setSubmitting] = useState(false);
  // Lien généré (passe l'UI en mode « lien prêt » : copier / envoyer).
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [generatedPaymentCaseId, setGeneratedPaymentCaseId] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  // Solde BED pré-calculé par le CRM (prix_total produit − Σ acompte BPI payé).
  // Lecture seule : chargé à l'ouverture, jamais saisi (GAP-1 / CDC §A).
  const [solde, setSolde] = useState<BedSoldeBreakdown | null>(null);
  const [loadingSolde, setLoadingSolde] = useState(false);
  const [soldeError, setSoldeError] = useState<string | null>(null);

  const {
    candidates,
    loading: loadingCandidates,
    error: candidatesError,
  } = usePaymentCaseContacts({
    paymentCaseId: open ? paidBpiCaseId : undefined,
  });

  const form = useForm<BedLinkFormValues>({
    resolver: zodResolver(bedLinkSchema),
    defaultValues: {
      payerContactId: '',
      locale: 'fr',
    },
  });

  // Réinitialise l'état à chaque ouverture (le dialog est monté en permanence)
  // et récupère le solde pré-calculé serveur-side pour l'afficher figé.
  useEffect(() => {
    if (!open) return;
    setGeneratedLink(null);
    setGeneratedPaymentCaseId(null);
    setSolde(null);
    setSoldeError(null);
    form.reset({ payerContactId: '', locale: 'fr' });

    let cancelled = false;
    setLoadingSolde(true);
    fetchBedSolde(paidBpiCaseId)
      .then((result) => {
        if (cancelled) return;
        if (result.success && result.solde) {
          setSolde(result.solde);
        } else {
          setSoldeError(result.error ?? 'Erreur lors du calcul du solde BED.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSolde(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, form, fetchBedSolde, paidBpiCaseId]);

  const payerOptions = useMemo(
    () => [
      { value: '', label: 'Aucun payeur renseigné' },
      ...candidates.map((candidate) => ({
        value: candidate.contact_id,
        label: formatCandidateLabel(candidate),
      })),
    ],
    [candidates]
  );

  async function handleGenerate(values: BedLinkFormValues) {
    if (!solde) {
      toast.error('Le solde BED doit être calculé avant de générer le lien.');
      return;
    }
    setSubmitting(true);
    // Montant transmis à titre informatif : la RPC RECALCULE le solde
    // serveur-side (00142) et ignore cette valeur. On passe le solde pré-calculé
    // pour rester cohérent, jamais une saisie libre.
    const expectedSoldeCents = solde.soldeCents;
    const result = await generateBedLink({
      crewId,
      contactId,
      amountCents: expectedSoldeCents,
      payerContactId: values.payerContactId || undefined,
      // BPI parent explicite : lève l'ambiguïté produit côté RPC (contact
      // ayant payé plusieurs BPI).
      parentBpiCaseId: paidBpiCaseId,
    });

    if (result.success && result.bedLink && result.paymentCaseId) {
      setGeneratedLink(result.bedLink);
      // payment_case_id remonté directement par la RPC (SECURITY DEFINER) :
      // c'est désormais la SEULE donnée transmise à l'envoi (avec la locale),
      // l'Edge send-bed-link résout destinataire + token côté serveur.
      setGeneratedPaymentCaseId(result.paymentCaseId);
      toast.success('Lien de paiement BED généré');
      // Réutilisation d'un BED dont le montant est figé (pending/partial) :
      // le solde recalculé n'a PAS été appliqué, on avertit l'admin.
      if (
        result.reused &&
        !result.amountUpdated &&
        typeof result.amountCents === 'number' &&
        result.amountCents !== expectedSoldeCents
      ) {
        toast.warning(
          `Lien existant réutilisé : montant inchangé (${(result.amountCents / 100).toLocaleString('fr-FR')} €)`
        );
      }
      // PAS de onGenerated() ici : le refresh est différé à la fermeture du
      // dialog pour ne pas le démonter pendant que l'admin copie/envoie le lien.
    } else {
      toast.error(result.error ?? 'Erreur lors de la génération du lien BED');
    }
    setSubmitting(false);
  }

  async function handleCopy() {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
      toast.success('Lien copié dans le presse-papiers');
    } catch {
      toast.error('Impossible de copier le lien.');
    }
  }

  async function handleSendEmail() {
    if (!generatedPaymentCaseId) {
      toast.error("Le lien doit être généré avant l'envoi.");
      return;
    }

    setSendingEmail(true);
    try {
      // Destinataire (participant : pilote en duo, sinon contact solo) et
      // token actif résolus côté SERVEUR par l'Edge (TICKET-BED-SEC-01) : le
      // navigateur n'envoie que le dossier et la langue de l'email.
      const { data, error: invokeError } = await supabase.functions.invoke('send-bed-link', {
        body: {
          payment_case_id: generatedPaymentCaseId,
          locale: form.getValues('locale'),
        },
      });

      if (invokeError) throw invokeError;

      const recipientEmail = (data as { recipient_email?: string } | null)?.recipient_email;
      toast.success(
        recipientEmail ? `Lien BED envoyé à ${recipientEmail}` : 'Lien BED envoyé au participant'
      );
    } catch (err) {
      console.error('[BedLinkDialog] send-bed-link error:', err);
      toast.error(await mapSendBedLinkError(err));
    } finally {
      setSendingEmail(false);
    }
  }

  function handleClose(value: boolean) {
    if (submitting || sendingEmail) return;
    onOpenChange(value);
    // Une génération a eu lieu pendant cette session du dialog : on rafraîchit
    // la liste des payment_cases SEULEMENT maintenant (à la fermeture), pour
    // que l'écran lien (copier/envoyer) reste utilisable jusqu'au bout.
    if (!value && generatedPaymentCaseId) {
      onGenerated();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Générer un lien de paiement BED</DialogTitle>
          <DialogDescription>
            Solde restant dû — <strong>{prospectName}</strong> — BED {productName}
          </DialogDescription>
        </DialogHeader>

        {generatedLink ? (
          // Mode « lien prêt » : copier / envoyer.
          <div className="flex flex-col gap-4">
            <div className="rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-800">
              Le lien de paiement BED a été créé. Vous pouvez le copier ou l'envoyer par email au
              participant.
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-zinc-700">Lien de paiement</span>
              <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 font-mono text-xs break-all text-zinc-700">
                {generatedLink}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                onClick={handleCopy}
                disabled={sendingEmail}
                className="justify-center"
              >
                <Copy className="mr-2 size-4" />
                Copier le lien
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="justify-center bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Send className="mr-2 size-4" />
                {sendingEmail ? 'Envoi...' : 'Envoyer le lien BED'}
              </Button>
            </div>
          </div>
        ) : (
          // Mode saisie : montant + payeur + langue email.
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleGenerate)} className="flex flex-col gap-4">
              {/* Solde pré-calculé par le CRM (lecture seule, GAP-1 / CDC §A) :
                  prix total produit − acompte BPI payé. Jamais saisi. */}
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-zinc-700">Solde restant dû</span>
                {loadingSolde ? (
                  <p className="text-xs text-zinc-400">Calcul du solde...</p>
                ) : soldeError ? (
                  <p className="text-xs text-red-500">{soldeError}</p>
                ) : solde ? (
                  <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2.5">
                    <div className="text-2xl font-semibold text-zinc-900">
                      {formatCents(solde.soldeCents)}
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">
                      Prix total {formatCents(solde.prixTotalCents)} − acompte BPI payé{' '}
                      {formatCents(solde.acompteCents)}
                      {solde.reductionCents > 0 && (
                        <> − réduction {formatCents(solde.reductionCents)}</>
                      )}
                    </div>
                    <div className="mt-1 text-[11px] text-zinc-400">
                      Calculé automatiquement par le CRM et figé sur le dossier. Les options
                      choisies par le participant s'ajouteront à ce solde.
                    </div>
                  </div>
                ) : null}
              </div>

              {loadingCandidates ? (
                <p className="text-xs text-zinc-400">Chargement des contacts...</p>
              ) : candidatesError ? (
                <p className="text-xs text-red-500">{candidatesError}</p>
              ) : (
                <FormSelect
                  control={form.control}
                  name="payerContactId"
                  label="Payeur (optionnel)"
                  placeholder="Aucun payeur renseigné"
                  options={payerOptions}
                  disabled={submitting}
                />
              )}

              <FormSelect
                control={form.control}
                name="locale"
                label="Langue de l'email"
                options={EMAIL_LOCALES.map((l) => ({ value: l.value, label: l.label }))}
                required
                disabled={submitting}
              />

              <p className="text-xs text-zinc-400">
                Le solde est calculé et figé côté serveur. Le lien reste valable 30 jours.
              </p>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  disabled={submitting}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || loadingCandidates || loadingSolde || !solde}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  {submitting ? 'Génération...' : 'Générer le lien'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
